import { Breadcrumb, Callout, DirectionalHint, Icon, SearchBox, Stack, Text, useTheme } from '@fluentui/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useNavigate } from 'react-router';
import { useGetSettings, useSetInitialSettings } from '../_contexts/settings/settingsContext';
import { ApiCalls } from '../api/api';
import { CONFIGURATION_FULL } from '../constants/queryKeys';
import '../Styles/SettingsSearch.scss';
import { Category, TSettingsCategory } from './components/Dashboard';
import { Headliner } from './components/Headliner';
import { LanguageSettingsMessageBar } from './components/LanguageSettingsMessageBar';
import { SaveSettingsMessageBar } from './components/SaveSettingsMessageBar';
import { configurationConfig } from './config/config';
import { flattenCategories } from './helpers/flattenCategories';
import { useCurrentCategory } from './hooks/useCurrentCategory';
import { useUiContext } from '../components/Contexts/UIContext/uiCtx';
import { useConfigValuesState } from '../hooks/useConfigValue';

const Configuration = () => {
    const setInitialSettings = useSetInitialSettings();

    const { isLoading } = useQuery([CONFIGURATION_FULL], async () => ApiCalls.configurationFullList().then(res => res.data), {
        onSuccess: settings => {
            setInitialSettings(settings);
        },
    });

    const {
        initialData: {
            aggregatedUser: { originalUser },
            configuration: { timeEntryCorrectionsEnabled = false }, // Add feature flag access
        },
    } = useConfigValuesState();

    const config = useMemo(() => {
        const originalList = configurationConfig.children as TSettingsCategory[];

        if (originalUser.isAdmin) {
            // Filter based on feature flags for admins
            const filteredList = originalList.filter(child => {
                // Filter out Time Entry Corrections if feature is disabled
                if ('link' in child && child.link === 'time-entry-corrections') {
                    return timeEntryCorrectionsEnabled;
                }
                return true;
            });

            return {
                ...configurationConfig,
                children: filteredList,
            };
        } else if (originalUser.permissions?.delegates || originalUser.permissions?.user) {
            const accessableList = originalList.filter(({ link }) => link === 'resource-overview');
            return {
                ...configurationConfig,
                children: accessableList,
            };
        }

        return {
            ...configurationConfig,
            children: [],
        };
    }, [originalUser.isAdmin, originalUser.permissions?.delegates, originalUser.permissions?.user, timeEntryCorrectionsEnabled]);

    const goTo = useNavigate();
    const { breadCrumbs, category } = useCurrentCategory({ config });
    const flatConfigRoutes = useMemo(() => flattenCategories(config), [config]);
    const [showSearchCallout, setShowSearchCallout] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const onSearchChange = useCallback((notUsedEvent: never, newValue: string | undefined) => {
        setSearchInput(newValue?.trim() || '');
    }, []);
    const dismissSearchCallout = useCallback(() => setShowSearchCallout(false), []);
    const openSearchCallout = useCallback(() => setShowSearchCallout(true), []);
    const searchResults = useMemo(() => {
        const searchVal = searchInput.toLowerCase();
        if (!searchVal) {
            return null;
        }
        return (
            flatConfigRoutes
                // search on shortDesription and title (maybe route also???)
                .filter(({ shortDescription, title }) => shortDescription.toLowerCase().includes(searchVal) || title.toLowerCase().includes(searchVal))
                // inject an onClick handler on results
                .map(meta => {
                    return {
                        ...meta,
                        onClick: () => {
                            goTo(meta.route);
                            dismissSearchCallout();
                        },
                    };
                })
        );
    }, [dismissSearchCallout, flatConfigRoutes, goTo, searchInput]);

    const theme = useTheme();

    const containerRef = useRef<HTMLDivElement>(null);

    const { editableSettings, settings } = useGetSettings();

    const settingsHasChanged = useMemo(() => settings !== editableSettings, [editableSettings, settings]);

    const { isDarkmode } = useUiContext();

    const highlightStyle = useMemo(() => ({ background: isDarkmode ? 'blue' : 'yellow', padding: 0, color: 'inherit' }), [isDarkmode]);

    useLayoutEffect(() => {
        // Reset the scroll position when the category changes
        if (containerRef.current) {
            containerRef.current.scrollTop = 0;
        }
    }, [category]);

    return (
        <>
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: theme.semanticColors.bodyBackgroundHovered,
                    position: 'relative',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    scrollSnapType: 'y mandatory',
                    overscrollBehaviorX: 'contain',
                }}
                ref={containerRef}
            >
                <SaveSettingsMessageBar />
                {/* different from SaveSettingsMessageBar as the update should be saved on both backend and i18n */}
                <LanguageSettingsMessageBar />

                <Headliner category={category} />

                <Stack
                    styles={{
                        root: {
                            paddingTop: 10,
                            paddingLeft: 10,
                            // paddingBottom: 10,
                            position: 'sticky',
                            top: settingsHasChanged ? 32 : 0,
                            zIndex: 1,
                            backdropFilter: 'blur(8px)',
                        },
                    }}
                >
                    <Stack
                        horizontal
                        verticalAlign="center"
                        // horizontalAlign="space-between"
                        styles={{
                            root: {
                                width: '100%',
                                '& > div:nth-last-child(1)': {
                                    width: '100%',
                                },
                                '& > div:nth-last-child(1) > div > div': {
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                },
                                '.ms-Breadcrumb': {
                                    margin: 0,
                                },
                            },
                        }}
                    >
                        <Icon iconName={category.iconName} styles={{ root: { fontSize: '2rem' } }} />
                        <Breadcrumb items={breadCrumbs} />
                    </Stack>
                    <SearchBox
                        onChange={onSearchChange}
                        autoComplete="off"
                        placeholder={`Search..`}
                        styles={{
                            root: {
                                backgroundColor: 'none',
                            },
                        }}
                        onFocus={openSearchCallout}
                    />
                </Stack>
                <Stack styles={{ root: { position: 'relative', flexGrow: 1 } }}>
                    <Category category={category} loading={isLoading} />
                </Stack>
            </div>
            {showSearchCallout && (
                <Callout
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    isBeakVisible={false}
                    className={'search-callout-container'}
                    target=".ms-SearchBox"
                    onDismiss={dismissSearchCallout}
                    styles={{
                        container: {
                            '.search-result-item2:hover': {
                                backgroundColor: theme.semanticColors.menuItemBackgroundHovered,
                            },
                        },
                    }}
                >
                    <Text className={'search-result-header'}>SEARCH RESULTS</Text>
                    <div className={'search-result-container'}>
                        {searchResults?.length > 0 ? (
                            searchResults.map(({ route, iconName, title, breadCrumb, onClick }) => {
                                return (
                                    <div className={'search-result-item2'} key={route} onClick={onClick}>
                                        <div className={'search-result-parent2'}>
                                            <Icon iconName={iconName} />
                                            &nbsp; &nbsp;
                                            <Highlighter searchWords={[searchInput]} highlightStyle={highlightStyle} textToHighlight={title} />
                                        </div>
                                        <div className={'search-result-item-content2'}>
                                            <Text>
                                                <Highlighter searchWords={[searchInput]} highlightStyle={highlightStyle} textToHighlight={breadCrumb} />
                                            </Text>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={'search-no-result'}>No results</div>
                        )}
                    </div>
                </Callout>
            )}
        </>
    );
};

export default Configuration;
